import { useCallback, useMemo } from 'react'
import { v4 } from 'uuid'
import { ThreeEvent } from '@react-three/fiber'
import { useControlStore } from '@editorStores'
import useModelClickListenerV2 from '../hooks/useModelClickListenerV2'
import useModelSelectionV2 from '../hooks/useModelSelectionV2'
import useVerticalTransmitterVisibility from '../hooks/useVerticalTransmitterVisibility'
import { ModelMeshEventData, useModelMeshStore } from '../state/store'

interface Props {
  transmitterGuid: string
  onClick: ({
    guid,
    element_guid,
  }: {
    guid: string
    element_guid: string
  }) => void | { selectedGuids?: string[] }
  elementGuids?: string[]
}

const emptyArray: string[] = []

const VerticalTransmittersMeshConnector = ({ transmitterGuid, elementGuids, onClick }: Props) => {
  const stateId = useMemo(() => v4(), [])
  const actionMode = useControlStore(state => state.actionMode)
  const addSelectedGuids = useModelMeshStore(state => state.addSelectedGuids)

  const onClickCallback = useCallback(
    (guid: string, event: ThreeEvent<MouseEvent>, eventData: ModelMeshEventData) => {
      if (eventData.type !== 'vertical_transmitter') return

      const result = onClick?.({
        guid: eventData.transmitterGuid,
        element_guid: eventData.elementGuid,
      })
      if (result && 'selectedGuids' in result) {
        addSelectedGuids(stateId, result.selectedGuids || [])
      }
    },
    [onClick, addSelectedGuids, stateId],
  )

  const selectedGuids = useMemo(() => {
    return transmitterGuid ? [transmitterGuid] : []
  }, [transmitterGuid])

  useModelSelectionV2(selectedGuids, stateId)
  useVerticalTransmitterVisibility(elementGuids || emptyArray, stateId)
  useModelClickListenerV2('vertical_transmitter', onClickCallback, {
    enabled: actionMode !== 'hide',
    guids: 'global-vertical-transmitter-mesh',
  })

  return null
}

export { VerticalTransmittersMeshConnector }
